import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { NgbDropdownModule, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';

export interface DropdownOption {
  label: string;
  value: string;
  unit: string;
}

@Component({
  selector: 'app-kpi-widget',
  standalone: true,
  imports: [CommonModule, NgbTooltipModule, NgbDropdownModule],
  templateUrl: './kpi-widget.component.html',
  styleUrl: './kpi-widget.component.scss'
})
export class KpiWidgetComponent {
  @Input() title: string;
  @Input() isLoading: boolean;
  @Input() icon: string;
  @Input() totalValue: number;
  @Input() totalCost: number;
  @Input() difference: number;
  @Input() percentageDifference: number;
  @Input() costDifference: number;
  @Input() errorMessage: string = '';
  @Input() selectedDropdownValue: string = '';
  @Input() dropdownOptions: DropdownOption[] = [];
  @Input() hasDifference: boolean = true;
  @Input() dropdownEnabled: boolean = true;
  @Input() widgetDisabled: boolean = true;
  @Input() percentageDifferenceEnabled: boolean = true;
  @Input() hasError: boolean = false;
  @Input() isUptrendPositive: boolean = true;
  @Input() hasCost: boolean = true;
  @Input() hasCostDifference: boolean = true;
  @Input() hasFooter: boolean = true;
  @Input() isImageIcon: boolean = true;
  @Input() hasValueOnly: boolean = false;
  @Input() isMonthToDateOnly: boolean = false;
  @Input() valueFormatter: (value: number, unit?: string) => string;
  @Input() differenceFormatter: (differenceSign: string, difference: number, percentageSign: string, percentage: number) => string;
  @Input() costFormatter: (value: number) => string;
  @Input() costDifferenceFormatter: (value: number) => string;

  @Output() clicked: EventEmitter<void> = new EventEmitter<void>();
  @Output() dropdownChanged = new EventEmitter<DropdownOption>();

  dropdownIcon = 'assets/images/driver-management/arrowDownIcon.svg';
  rotation = 0;
  isOpen = false;

  ngOnInit(): void {
    this.isLoading = true;
  }

  rotateIcon(event: boolean) {
    this.isOpen = event;
    this.rotation = this.isOpen ? 180 : 0;
  }

  onOptionClick(option: DropdownOption, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    this.dropdownChanged.emit(option);
  }

  onWidgetClick() {
    if (this.widgetDisabled) return;
    this.clicked.emit();
  }
  
  formatDifference(difference: number, percentage: number): string {
    const differenceSign = difference > 0 ? '+' : difference < 0 ? '-' : '';
    const absDifference = Math.abs(difference);
    const percentageSign = percentage > 0 ? '+' : percentage < 0 ? '-' : '';
    const absPercentage = Math.abs(percentage);

    return this.hasDifference && difference !== null && difference !== undefined
      ? this.differenceFormatter(differenceSign, absDifference, percentageSign, absPercentage)
      : `${percentageSign}${absPercentage.toLocaleString('default', { maximumFractionDigits: 2 })}%`
  }

  formatPercentageDifference(): string {
    if (this.percentageDifference === null || this.percentageDifference === undefined) return 'N/A';
    return this.formatDifference(this.difference, this.percentageDifference);
  }

  isNADifference(): boolean {
    return this.percentageDifference === null || this.percentageDifference === undefined;
  }

  formatCostDifference(): string {
    return this.costDifferenceFormatter(this.costDifference);
  }

  formatValue(): string {
    const selectedOption = this.dropdownOptions.find(opt => opt.value === this.selectedDropdownValue);
    return this.valueFormatter(this.totalValue, selectedOption?.unit);
  }

  formatCost(): string {
    return this.costFormatter ? this.costFormatter(this.totalCost) : '';
  }

  isUptrend(value: number): boolean {
    return this.isUptrendPositive && value >= 0 || !this.isUptrendPositive && value < 0;
  }

  getSelectedOptionLabel(): string {
    const selectedOption = this.dropdownOptions.find(opt => opt.value === this.selectedDropdownValue);
    return selectedOption?.label || '';
  }
}